import { ApiErrorState } from '@/components/ApiErrorState/ApiErrorState'
import { AuthorizedRoute } from '@/components/AuthorizedRoute/AuthorizedRoute'
import { DataTag } from '@/components/DataTag/DataTag'
import SkeletonTable from '@/components/SkeletonTable'
import TableFooter from '@/components/TableFooter/TableFooter'
import { PageLayout } from '@/components/templates/PageLayout/PageLayout'
import { useClusterUpdates } from '@/contexts/useClusterUpdates'
import ClusterUpdateTaskStatusIcon from '@/features/ClusterUpdates/ClusterUpdateTaskStatus'
import type { BulkUpdatesResponse } from '@/generated/http-clients/v3ApiSchemas'
import { FIRST_PAGE } from '@/helpers/constants'
import {
  clusterUpdatesRootRoute,
  templatesRootRoute,
} from '@/router/rootRoutes'
import {
  Button,
  Card,
  Flex,
  Popover,
  Table,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import type { UseQueryResult } from '@tanstack/react-query'
import { createRoute, useNavigate } from '@tanstack/react-router'
import { type ChangeEvent, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { createHandleKeyDown } from '../../utils/handleKeyDown'
import styles from './clusterUpdateTable.module.css'
import { augmentBulkUpdateTask, bulkUpdateTaskMetadata } from './utils'

export const clusterUpdateTableRoute = createRoute({
  getParentRoute: () => clusterUpdatesRootRoute,
  path: '/',
  component: () => (
    <AuthorizedRoute>
      <ClusterUpdateTable />
    </AuthorizedRoute>
  ),
})

export function ClusterUpdateTable() {
  const navigate = useNavigate()
  const { t } = useTranslation(undefined, { useSuspense: false })
  const {
    clusterUpdates,
    approveBulkUpdateTask,
    rejectBulkUpdateTask,
    applyBulkUpdateTask,
  } = useClusterUpdates()

  const popoverTextareaRef = useRef(null)
  const approveButtonRef = useRef<HTMLButtonElement>(null)
  const applyButtonRef = useRef<HTMLButtonElement>(null)

  // Get page from URL or default to 1
  const [page, setPage] = useState(1)

  const [comment, setComment] = useState('')

  // Separate loading states for each button
  const [approveLoading, setApproveLoading] = useState(false)
  const [rejectLoading, setRejectLoading] = useState(false)
  const [applyLoading, setApplyLoading] = useState(false)

  const updateComment = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const updatedComment = event.target.value
    setComment(updatedComment)
  }

  const onApproveKeyDown = createHandleKeyDown(approveButtonRef)
  const onApplyKeyDown = createHandleKeyDown(applyButtonRef)

  function approveTask(bulkUpdateId: string) {
    setApproveLoading(true) // Set loading state for approve button
    return approveBulkUpdateTask(bulkUpdateId, comment)
      .then(() => {
        setComment('') // Reset comment state only on success
      })
      .finally(() => {
        setApproveLoading(false) // Reset loading state
      })
  }

  function rejectTask(bulkUpdateId: string) {
    setRejectLoading(true) // Set loading state for reject button
    return rejectBulkUpdateTask(bulkUpdateId, comment)
      .then(() => {
        setComment('') // Reset comment state only on success
      })
      .finally(() => {
        setRejectLoading(false) // Reset loading state
      })
  }

  function applyTask(bulkUpdateId: string) {
    setApplyLoading(true) // Set loading state for apply button
    return applyBulkUpdateTask(bulkUpdateId, comment)
      .then(() => {
        setComment('') // Reset comment state only on success
      })
      .finally(() => {
        setApplyLoading(false) // Reset loading state
      })
  }

  function handleNextPage() {
    if ((clusterUpdates?.data as BulkUpdatesResponse).has_next_page) {
      const newPage = page + 1
      setPage(newPage)
    }
  }

  function handlePreviousPage() {
    if (page > 1) {
      const newPage = page - 1
      setPage(newPage)
    }
  }

  function goToFirstPage() {
    setPage(FIRST_PAGE)
  }

  function goToLastPage() {
    const newPage =
      (clusterUpdates?.data as BulkUpdatesResponse).total_pages ?? page
    setPage(newPage)
  }

  // Use useMemo to transform the clusterUpdates.data array outside the rendering loop
  const augmentedTasks = useMemo(() => {
    if (clusterUpdates?.data) {
      return (clusterUpdates.data as BulkUpdatesResponse).bulkUpdates.map(
        augmentBulkUpdateTask,
      )
    }
    return []
  }, [clusterUpdates?.data])

  const skeletonWidths = [32, 250, 100, 100, 100]

  const headerAlignment = useMemo(() => {
    return clusterUpdates.isLoading ? 'left' : 'right'
  }, [clusterUpdates.isLoading])

  return (
    <PageLayout
      title={t('sidebar.hiveUpdates')}
      data-testid="cluster-update-table"
    >
      <Card.Root w="100%">
        <Table.Root variant="outline">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader width="32px" paddingRight="0" />
              <Table.ColumnHeader pl={4}>
                {t('bulkUpdateTable.columns.bulkUpdateId')}
              </Table.ColumnHeader>
              <Table.ColumnHeader textAlign={headerAlignment}>
                {t('bulkUpdateTable.columns.template')}
              </Table.ColumnHeader>
              <Table.ColumnHeader textAlign={headerAlignment}>
                {t('bulkUpdateTable.columns.clusters')}
              </Table.ColumnHeader>
              <Table.ColumnHeader textAlign={headerAlignment}>
                {t('bulkUpdateTable.columns.actions')}
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {clusterUpdates.isLoading && (
              <SkeletonTable skeletonWidths={skeletonWidths} />
            )}

            {(clusterUpdates.isError || !clusterUpdates.data) && (
              <Table.Row>
                <Table.Cell colSpan={skeletonWidths.length}>
                  <ApiErrorState
                    apiResource={clusterUpdates as unknown as UseQueryResult}
                    errorTitle={t('bulkUpdateTable.errorTitle')}
                    errorDescription={t('bulkUpdateTable.errorDescription')}
                  />
                </Table.Cell>
              </Table.Row>
            )}

            {clusterUpdates.isSuccess &&
              (clusterUpdates?.data as BulkUpdatesResponse).bulkUpdates
                .length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan={skeletonWidths.length}>
                    <Text>{t('bulkUpdateTable.noResults')}</Text>
                  </Table.Cell>
                </Table.Row>
              )}

            {clusterUpdates.isSuccess &&
              augmentedTasks.length > 0 &&
              augmentedTasks.map((augmentedTask) => {
                const bulkUpdateMetadata = bulkUpdateTaskMetadata(augmentedTask)
                const statusText = bulkUpdateMetadata
                  ? t(`bulkUpdateTable.statusTexts.${augmentedTask.status}`, {
                      when: bulkUpdateMetadata.when,
                      by: bulkUpdateMetadata.by,
                    })
                  : t('bulkUpdateTable.unknownStatus', {
                      status: augmentedTask.status,
                    })

                return (
                  <Table.Row
                    key={augmentedTask.bulkUpdateId}
                    className={styles.trAlignContentTop}
                    _hover={{
                      backgroundColor: 'var(--chakra-colors-shell-muted)',
                    }}
                  >
                    <Table.Cell
                      data-testid="bulk-update-status"
                      className={styles.trAlignContentTopTdIcon}
                      width="32px"
                    >
                      <ClusterUpdateTaskStatusIcon task={augmentedTask} />
                    </Table.Cell>
                    <Table.Cell data-testid="bulk-update-id">
                      <Flex direction="column" gap="1">
                        <DataTag
                          fontWeight={400}
                          value={augmentedTask.bulkUpdateId}
                          hasCopy={true}
                          maxWidthPx={250}
                          onClickBody={() =>
                            navigate({
                              to: `${clusterUpdatesRootRoute.to}/${augmentedTask.bulkUpdateId}`,
                            })
                          }
                        >
                          {augmentedTask.bulkUpdateId}
                        </DataTag>
                        <Text fontSize="sm" color="gray">
                          {statusText}
                        </Text>
                      </Flex>
                    </Table.Cell>
                    <Table.Cell
                      data-testid="bulk-update-template"
                      className={styles.tdAlignRight}
                    >
                      <Flex direction="column" gap="1">
                        <DataTag
                          fontWeight={400}
                          value={augmentedTask.templateId}
                          hasCopy={true}
                          maxWidthPx={250}
                          onClickBody={() =>
                            navigate({
                              to: `${templatesRootRoute.to}/${augmentedTask.templateId}`,
                            })
                          }
                        >
                          {augmentedTask.templateId}
                        </DataTag>
                        <Text fontSize="sm" color="gray">
                          {augmentedTask.templateName ?? ''}
                        </Text>
                      </Flex>
                    </Table.Cell>
                    <Table.Cell
                      data-testid="bulk-update-clusters"
                      width={20}
                      className={styles.tdAlignRight}
                    >
                      <Flex direction="column" gap="1">
                        <Text fontSize="sm" color="gray">
                          {augmentedTask.clusterIds.length}
                        </Text>
                      </Flex>
                    </Table.Cell>
                    <Table.Cell
                      data-testid="bulk-update-actions"
                      className={styles.trAlignContentTopTdActions}
                    >
                      {augmentedTask.isSubmitted && (
                        <Popover.Root
                          positioning={{ placement: 'bottom-end' }}
                          initialFocusEl={() => popoverTextareaRef.current}
                        >
                          <Popover.Trigger>
                            <Button variant="outline" size="sm">
                              {t('bulkUpdateTable.actions.review')}
                            </Button>
                          </Popover.Trigger>
                          <Popover.Positioner>
                            <Popover.Content>
                              <Popover.Header
                                textAlign="left"
                                border={0}
                                pt={4}
                              >
                                <Text fontWeight={500}>
                                  {t('bulkUpdateTable.popovers.review.title')}
                                </Text>
                                <Popover.CloseTrigger
                                  color="icon.icon-base"
                                  top={2}
                                />
                              </Popover.Header>
                              <Popover.Body>
                                <Textarea
                                  placeholder={
                                    t('bulkUpdateTable.popovers.placeholder') ??
                                    ''
                                  }
                                  value={comment}
                                  onChange={updateComment}
                                  onKeyDown={onApproveKeyDown}
                                  ref={popoverTextareaRef}
                                />
                              </Popover.Body>
                              <Popover.Footer
                                display="flex"
                                justifyContent="end"
                                border={0}
                                gap={2}
                              >
                                <Button
                                  variant="solid"
                                  colorPalette="red"
                                  onClick={() =>
                                    rejectTask(augmentedTask.bulkUpdateId)
                                  }
                                  loading={rejectLoading}
                                  disabled={rejectLoading}
                                >
                                  {t(
                                    'bulkUpdateTable.popovers.review.footer.reject',
                                  )}
                                </Button>
                                <Button
                                  variant="solid"
                                  colorPalette="green"
                                  onClick={() =>
                                    approveTask(augmentedTask.bulkUpdateId)
                                  }
                                  ref={approveButtonRef}
                                  loading={approveLoading}
                                  disabled={approveLoading}
                                >
                                  {t(
                                    'bulkUpdateTable.popovers.review.footer.approve',
                                  )}
                                </Button>
                              </Popover.Footer>
                            </Popover.Content>
                          </Popover.Positioner>
                        </Popover.Root>
                      )}

                      {augmentedTask.isApproved && (
                        <Popover.Root
                          positioning={{ placement: 'bottom-end' }}
                          initialFocusEl={() => popoverTextareaRef.current}
                        >
                          <Popover.Trigger>
                            <Button variant="outline" size="sm">
                              {t('bulkUpdateTable.actions.apply')}
                            </Button>
                          </Popover.Trigger>
                          <Popover.Positioner>
                            <Popover.Content>
                              <Popover.Header
                                textAlign="left"
                                border={0}
                                pt={4}
                              >
                                <Text fontWeight={500}>
                                  {t('bulkUpdateTable.popovers.apply.title')}
                                </Text>
                                <Popover.CloseTrigger
                                  color="icon.icon-base"
                                  top={2}
                                />
                              </Popover.Header>
                              <Popover.Body>
                                <VStack alignItems="left" gap={4}>
                                  <Text
                                    color="text.text-subtle"
                                    textAlign="left"
                                  >
                                    {t(
                                      'bulkUpdateTable.popovers.apply.description',
                                      {
                                        count: augmentedTask.clusterIds.length,
                                      },
                                    )}
                                  </Text>
                                  <Textarea
                                    placeholder={
                                      t(
                                        'bulkUpdateTable.popovers.placeholder',
                                      ) ?? ''
                                    }
                                    value={comment}
                                    onChange={updateComment}
                                    onKeyDown={onApplyKeyDown}
                                    ref={popoverTextareaRef}
                                  />
                                </VStack>
                              </Popover.Body>
                              <Popover.Footer
                                display="flex"
                                justifyContent="end"
                                border={0}
                                gap={2}
                              >
                                <Button variant="ghost" mr={3}>
                                  {t(
                                    'bulkUpdateTable.popovers.apply.footer.cancel',
                                  )}
                                </Button>
                                <Button
                                  variant="solid"
                                  onClick={() =>
                                    applyTask(augmentedTask.bulkUpdateId)
                                  }
                                  ref={applyButtonRef}
                                  loading={applyLoading}
                                  disabled={applyLoading}
                                >
                                  {t(
                                    'bulkUpdateTable.popovers.apply.footer.confirm',
                                  )}
                                </Button>
                              </Popover.Footer>
                            </Popover.Content>
                          </Popover.Positioner>
                        </Popover.Root>
                      )}
                    </Table.Cell>
                  </Table.Row>
                )
              })}
          </Table.Body>
        </Table.Root>
      </Card.Root>

      {clusterUpdates.isSuccess && (
        <TableFooter
          page={page}
          totalPages={
            (clusterUpdates?.data as BulkUpdatesResponse).total_pages ?? page
          }
          hasNextPage={Boolean(
            (clusterUpdates?.data as BulkUpdatesResponse).has_next_page,
          )}
          onFirstPage={goToFirstPage}
          onPreviousPage={handlePreviousPage}
          onNextPage={handleNextPage}
          onLastPage={goToLastPage}
        />
      )}
    </PageLayout>
  )
}
